import { Component, OnInit, Inject } from '@angular/core';
import { Common, _, core, InfoService } from '@icc/shared/helpers';
import { PageComponent, ICC_PAGE_DATA, ModalService, ConfiguratorsDataLoaded } from '@icc/configurator/shared';
import { SharedFacade } from '@icc/configurator/shared';
import { Profile, ProfileType, Reinforcement } from '@icc/window-door/types';
import { BrowserProfilesService } from '../profiles.service';
import { ProfilesPriceService } from '@icc/construction-elements/configurator';
import { CurrentConfiguratorService } from '@icc/configurator/shared';
import { IccProfileSideColors, IccSideColors, Wood } from '@icc/construction-elements/types';
import { ConfigurationsService } from '@icc/configurator/shared';
import { AppConfigFactory, APP_CONFIG, TranslateService, ConfiguratorsDataService, EventBusService, IssuesService } from '@icc/shared';
import { iccListItem, iccListTab, UnitConverterService } from '@icc/configurator/ui';
import { PriceBaseService } from '@icc/price/b2b';
import { PriceColorsService, ProfileColorsService, ProfilesListPageComponent, iccProfileListItem } from '@icc/construction-elements/configurator';
import { ProfileOptionsPageComponent } from '../profile-options-page/profile-options-page.component';
import { BehaviorSubject, Subscription} from 'rxjs';
import { WindowAccessoriesService } from '../../accessories/accessories.service';
import { DoorPortalsService } from '../../door-portals/door-portals.service';
import { ProfileInfoComponent } from '@icc/construction-elements/configurator';
import { DependenciesService } from '../../dependencies/dependencies.service';
import { SizesService } from '../../door/sizes/sizes.service';
import { ReinforcementsModalService } from '../../reinforcements/reinforcements-modal.service';
import { ReinforcementsService } from '../../reinforcements/reinforcements.service';
import { IssueLevel } from '@icc/shared/helpers';
import { ProfilesUpdateService } from '../profiles-update.service';

interface BlockFrameProfile {
    ids: number[],
    conditionsType: (string | undefined)[],
    dependencyId: number
}

@Component({
    selector: 'icc-window-profiles-list-page',
    templateUrl: './profiles-list-page.component.html',
    styleUrls: ['./profiles-list-page.component.scss'],
    providers: [ProfilesUpdateService],
    standalone: false
})
export class WindowProfilesListPageComponent extends ProfilesListPageComponent implements OnInit {
    private isApplyingVariableCouplingFromOptions = false;
        /**
         * Zwraca sklampowaną szerokość oraz indeks zakresu dla widthOutRanges.
         */
        private getProfileWidthAndRange(profile: Profile, selectedWidth: number) {
            const bounds = this.getWidthBounds(profile);
            const clampedWidth = this.clampWidth(selectedWidth, bounds);
            let rangeIndex = -1;
            const widthOutRanges = this.getNormalizedWidthOutRanges(profile?.widthOutRanges);
            if (profile.widthOutType && widthOutRanges.length) {
                rangeIndex = widthOutRanges.findIndex((range: any) => {
                    const from = Number(range?.from);
                    const to = Number(range?.to);
                    return !isNaN(from) && !isNaN(to) && clampedWidth >= from && clampedWidth <= to;
                });
            }
            return { clampedWidth, rangeIndex };
        }

    private getNormalizedWidthOutRanges(widthOutRanges: any): any[] {
        if (Array.isArray(widthOutRanges)) {
            return widthOutRanges;
        }

        if (typeof widthOutRanges === 'string') {
            const parsed = core.parseJson(widthOutRanges);
            return Array.isArray(parsed) ? parsed : [];
        }

        return [];
    }
    selectedTypeTab = 'fixed_mullion';
    blockedExtensions: { types: any; options: any; }[] = [];
    blockedFrameProfiles: BlockFrameProfile[] = []
    profiles: iccProfileListItem[] = [];

    dimensionUnit = this.unitConverterService.getUnit();
    private dependencies: {
        blockedExtension?: boolean; id: number; conditions: { type?: string, value?: any; }[];
    }[] = [];

    /**
     * Pobiera profil coupling z usedProfiles i kopiuje go, jeśli istnieje i ma widthOutType.
     * @param conf konfiguracja
     * @param coupling coupling
     */
    private getCouplingProfileFromUsedProfiles(conf: any, coupling: any): Profile | undefined {
        const usedProfile = (conf as any).usedProfiles?.find((p: any) => Number(p.id) === Number(coupling.profileId));
        if (usedProfile && usedProfile.widthOutType) {
            const profile = core.copy(usedProfile);
            (profile as any).originalType = 'coupling';
            return profile;
        }
        return undefined;
    }

    private getFrameProfileFromCurrentConfiguration(
        conf: any,
        profileItem: iccProfileListItem,
    ): Profile | undefined {
        if (!this.pageData.frame || this.pageData.framePosition == null) {
            return undefined;
        }

        const selectedBaseProfileId = Number(profileItem.id);
        const currentSelectedProfile = this.pageData.selectedProfile;
        const currentSelectedBaseProfileId = Number(
            currentSelectedProfile?._clonedFrom
            ?? currentSelectedProfile?.originalProfile
            ?? currentSelectedProfile?.id
        );

        if (
            currentSelectedProfile?.widthOutType
            && Number.isFinite(currentSelectedBaseProfileId)
            && currentSelectedBaseProfileId === selectedBaseProfileId
        ) {
            const profile = core.copy(currentSelectedProfile);
            (profile as any).originalType = this.getEffectiveOriginalType(profile, conf, this.pageData.mullionId);
            return profile;
        }

        const currentFrame = conf?.Frames?.find((f: any) => Number(f.id) === Number(this.pageData.frame?.id));
        const currentFrameSide = currentFrame?.frame?.[this.pageData.framePosition];
        const currentFrameProfileId = currentFrameSide?.alt?.profileId ?? currentFrameSide?.profileId;
        const currentFrameProfile = conf?.UsedProfiles?.find(
            (p: any) => Number(p.id) === Number(currentFrameProfileId)
        );
        const currentFrameBaseProfileId = Number(
            currentFrameProfile?._clonedFrom
            ?? currentFrameProfile?.originalProfile
            ?? currentFrameProfile?.id
        );

        if (
            currentFrameProfile?.widthOutType
            && Number.isFinite(currentFrameBaseProfileId)
            && currentFrameBaseProfileId === selectedBaseProfileId
        ) {
            const profile = core.copy(currentFrameProfile);
            (profile as any).originalType = 'frame';
            return profile;
        }

        return undefined;
    }

    constructor(
        protected sharedFacade: SharedFacade,
        protected profilesService: BrowserProfilesService,
        protected profilesPriceService: ProfilesPriceService,
        protected priceBaseService: PriceBaseService,
        protected translateService: TranslateService,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        protected CurConfService: CurrentConfiguratorService,
        protected configurationsService: ConfigurationsService<'door'>,
        protected profileColorsService: ProfileColorsService,
        protected modalService: ModalService,
        @Inject(ICC_PAGE_DATA)
        protected pageData: {
            profiles: Profile[];
            profilesPrices: any[];
            selectedProfile: Profile;
            system: any | null;
            type: ProfileType;
            currency: any;
            color?: IccProfileSideColors;
            wood?: Wood;
            reinforcement?: Reinforcement;
            profileCategories?: iccListTab[];
            hiddenPrice?: boolean;
            isDefaultLength?: boolean;
            length?: number;
            frame?: any;
            framePosition?: number;
            mullionId?: number | null;
        },
        protected unitConverterService: UnitConverterService,
        protected infoService: InfoService,
        protected accessoriesService: WindowAccessoriesService,
        protected doorPortalsService: DoorPortalsService,
        protected configuratorsDataService: ConfiguratorsDataService,
        protected eventBusService: EventBusService,
        protected dependenciesService: DependenciesService,
        protected sizesService: SizesService,
        protected issuesService: IssuesService,
        protected priceColorsService: PriceColorsService,
        protected reinforcementsModalService: ReinforcementsModalService,
        protected reinforcementsService: ReinforcementsService,
        protected windowProfilesUpdateService: ProfilesUpdateService,
    ) {
        super(
            sharedFacade,
            profilesService,
            profilesPriceService,
            priceBaseService,
            translateService,
            config,
            CurConfService,
            configurationsService,
            profileColorsService,
            modalService,
            issuesService,
            pageData,
            unitConverterService,
            configuratorsDataService,
            priceColorsService,
        );
    }


    hasReinforcementsForProfile(profile: Profile): boolean {
        return this.reinforcementsService.hasReinforcementsForProfile(profile);
    }

    openReinforcementsForFrameProfile(item: iccProfileListItem): void {
        const currentReinforcement = (item as any).reinforcement ?? this.pageData.reinforcement ?? null;
        this.reinforcementsModalService
            .openReinforcementsModal(
                Number(item.id),
                currentReinforcement,
                this.pageData.currency,
                this.pageData.length,
            )
            .then((reinforcement: Reinforcement | false) => {
                if (reinforcement !== false) {
                    const newReinforcement = reinforcement.id === null
                        ? null
                        : { ...core.copy(reinforcement), automaticSelection: false };
                    (item as any).reinforcement = newReinforcement;
                    const conf = this.configurationsService.conf.Current;
                    if (conf && this.pageData.frame) {
                        if (this.pageData.framePosition != null) {
                            this.reinforcementsService.setFrameSideReinforcement(
                                conf,
                                this.pageData.frame,
                                this.pageData.framePosition,
                                newReinforcement,
                            );
                        } else {
                            this.reinforcementsService.setAllFrameSidesReinforcement(
                                conf,
                                this.pageData.frame,
                                newReinforcement,
                            );
                        }
                        (this.pageData as any).reinforcement = newReinforcement;
                    }
                }
            });
    }

    
    ngOnInit() {
        super.ngOnInit();
        this.dependencies = this.configuratorsDataService.data.dependencies || [];
        const blockedItems = this.configurationsService.conf.Current.Dependencies?.filter(
            (p) => p.type === 'blockade_to_configuration'
        ) || [];
        this.hiddenPrice =
           (this.config().preset === 'b2c' && this.config().hidePricesInB2cConfigurator)
           || (this.config().IccConfig.Configurators.hideProfilePrice);
        this.dependencies.forEach((dependency) => {
            blockedItems.forEach(blocked => {
                if (dependency && blocked && dependency.id === Number(String(blocked.id).split('-')[0])) {
                    dependency.conditions.forEach((condition) => {
                        if (condition.type === 'extension' && dependency.blockedExtension) {
                            this.blockedExtensions.push(
                                {
                                    types: condition.value.extensionTypes,
                                    options: condition.value?.extensionOptions
                                }
                            )
                        }
                    });
                }
            })
            if(dependency.conditions.some(condition => condition.type === 'frameProfile')) {
                this.blockedFrameProfiles.push({
                    ids: dependency.conditions.find(condition => condition.type === 'frameProfile')?.value || [],
                    conditionsType: dependency.conditions.map(condition => condition.type),
                    dependencyId: dependency.id
                });
            }
        })
        this.markAsDisabled();
    }

    markAsDisabled(conf = this.configurationsService.conf.Current) {
        this.profiles.forEach(profile => {
            if (['extension', 'frame'].indexOf(profile.type) === -1 && !profile.profileCategoryId) return;

            const category = this.pageData.profileCategories?.find(
                (c) =>
                    c?.quantity_restriction &&
                    c?.max_quantity &&
                    profile.profileCategoryId === c?.id
            );
                if (category && !this.blockedExtensions.length && !this.blockedFrameProfiles.length) {
                    return;
                } else {
                    if (this.isExtensionAvailable(Number(profile.id), profile.side)) {
                        profile.disabled = false;
                    } else {
                        profile.disabled = true;
                    }
                }
            }
        );
    }

    isExtensionAvailable(profileId: number, side?: 'left' | 'right' | 'top' | 'bottom', conf = this.configurationsService.conf.Current) {
        const handlePosition = conf.Sashes?.find((k) => k.type?.handle_position)?.type?.handle_position;

        let isExtensionViable = true;
        if(!(Array.isArray(this.blockedExtensions) && this.blockedExtensions.length)) {
            this.blockedExtensions.forEach((e) => {
                const fromHingeSide = e.options?.includes('from_hinge_side');
                e.types.forEach((t: { id: number | "ALL_EXTENSIONS" }) => {
                    if (fromHingeSide && (t.id === profileId || t.id === "ALL_EXTENSIONS")) {
                        if (handlePosition === 'L' && side === 'right') {
                            isExtensionViable = false;
                        } else if (handlePosition === 'R' && side === 'left') {
                            isExtensionViable = false;
                        }
                    } else if (t.id === profileId || t.id === "ALL_EXTENSIONS") {
                        isExtensionViable = false;
                    }
                });
            });
        }

        if(isExtensionViable && this.blockedFrameProfiles.length) {
            this.blockedFrameProfiles.forEach(blockedExtension => {
                if(blockedExtension.ids.includes(profileId) && this.profileIsBlockByDependency(conf, profileId, blockedExtension)) {
                    isExtensionViable = false;
                }
            });
        }

        return isExtensionViable;
    }

    profileIsBlockByDependency(conf: any, profileId: number, blockedExtension: BlockFrameProfile) {
        if(blockedExtension.conditionsType.includes('doorWidth') || blockedExtension.conditionsType.includes('doorHeight')) {
            const copyCof = core.copy(conf);
            const profile = this.profilesService.getProfile(profileId);
            copyCof.UsedProfiles = copyCof.UsedProfiles.filter((p: any) => p.type !== 'frame')
            copyCof.UsedProfiles.push(profile)
            copyCof.ProfileSet.frameSide = profileId;
            const doorSizes = copyCof.doorSizes;
            const { width, height } = this.sizesService.getConstructionDimensions(doorSizes, copyCof);
            copyCof.Width = width;
            copyCof.Height = height;
            this.dependenciesService.process(copyCof);
            if(copyCof.Dependencies.some((dependency: any) => Number(String(dependency.id).split('-')[0]) === blockedExtension.dependencyId)) {
                return true;
            }
        }

        return false;
    }

    showInvalidAccessoriesForFrameInfo(invalidAccessories: string[], profileItem: iccProfileListItem) {
        this.infoService.openConfirmModal(
            this.translateService.instant('WINDOW|Niedostępne dodatki dla profilu ramy'),
            this.translateService.instant(`WINDOW|Po zmianie profilu następujące dodatki zostaną usunięte:`) + ' ' + invalidAccessories.filter(name => ' ' + name),
            [
                {
                    name: this.translateService.instant('INTERFACE|Anuluj'),
                    callback: () => {},
                    accent: true,
                },
                {
                    name: this.translateService.instant('INTERFACE|Kontynuuj'),
                    callback: () => {
                        this.accessoriesService.removeInvalidFrameDependentAccessories(profileItem.id);
                        this.selectProfile(profileItem);
                    },
                },
            ]
        );
    }

    changeProfile(profileItem: iccProfileListItem) {
        let invalidAccessoriesToFrameProfile;
        let profile: Profile | undefined;
        const conf = this.configurationsService.conf.Current;
        let coupling: any;
        const isVariableWidthMuntinSelection =
            !!profileItem.widthOutType
            && !!this.pageData.mullionId
            && profileItem.type !== 'coupling'
            && profileItem.originalType !== 'coupling';
        const isVariableWidthFrameSelection =
            !!profileItem.widthOutType
            && profileItem.type === 'frame';
        const shouldDeferVariableWidthToOptionsEarly =
            (profileItem.type === 'coupling'
                || isVariableWidthMuntinSelection
                || isVariableWidthFrameSelection)
            && profileItem.widthOutType
            && !this.isApplyingVariableCouplingFromOptions;
        if (profileItem.widthOutType && (profileItem.type === 'coupling' || profileItem.originalType === 'coupling') && conf) {
            if (this.pageData.selectedProfile && this.pageData.selectedProfile.id < 0) {
                coupling = this.getCouplingByProfileId(conf, Number(this.pageData.selectedProfile.id));
                if (!shouldDeferVariableWidthToOptionsEarly && coupling && this.pageData.selectedProfile?.originalProfile !== profileItem.id) {
                    coupling.profileId = profileItem.id;
                }
            } else {
                coupling = this.getCouplingByProfileId(conf, Number(profileItem.id));
            }
            if (coupling && !shouldDeferVariableWidthToOptionsEarly) {
                profile = this.getCouplingProfileFromUsedProfiles(conf, coupling);
            } else if (!shouldDeferVariableWidthToOptionsEarly) {
                if (profileItem.widthOutType) {
                    this.markProfileForCouplingCloning(Number(profileItem.id), profileItem.selectedWidth);
                }
            }
        }
        if (!profile && profileItem.widthOutType && this.pageData.mullionId && conf && profileItem.type !== 'coupling' && profileItem.originalType !== 'coupling') {
            const sashesMullions = (conf.Sashes || []).reduce((acc: any[], sash: any) => acc.concat(sash?.intMullions || []), [] as any[]);
            const targetMullion = sashesMullions.concat(conf.Mullions || []).find((m: any) => Number(m.id) === Number(this.pageData.mullionId));
            if (targetMullion) {
               const mullionProfile = (conf as any).UsedProfiles?.find((p: any) => Number(p.id) === Number(targetMullion.profileId));
                const selectedBaseProfileId = Number(profileItem.id);
                const cloneBaseProfileId = Number(
                    mullionProfile?._clonedFrom
                    ?? mullionProfile?.originalProfile
                    ?? mullionProfile?.id
                );
                const isSameSelectedProfile = Number.isFinite(cloneBaseProfileId)
                    && cloneBaseProfileId === selectedBaseProfileId;
                if (mullionProfile && mullionProfile.widthOutType && isSameSelectedProfile) {
                    profile = core.copy(mullionProfile);
                    (profile as any).originalType = this.getEffectiveOriginalType(profile, conf, targetMullion.id);
                }
            }
        }
        if (!profile && profileItem.widthOutType && profileItem.type === 'frame' && conf) {
            profile = this.getFrameProfileFromCurrentConfiguration(conf, profileItem);
        }
        if (!profile) {
            const original = this.pageData.profiles.find((p) => Number(p.id) === Number(profileItem.id));
            if (original) {
                profile = core.copy(original);
                (profile as any).originalType = this.getEffectiveOriginalType(profile, this.configurationsService.conf.Current, this.pageData.mullionId);
            }
        }

        if (profile && profile.type === 'coupling' && !(profile as any).originalType) {
            profile = core.copy(profile);
        }
        
        if (profile) {
            const clonedFrom = (profile as any)._clonedFrom;
            let originalProfile;
            if(profile.id > -1)
            {
                originalProfile = profile.id; 
            }
            else if(profile. originalProfile){
                originalProfile = profile.originalProfile;
            }
            
            let parent: any = this.pageData.profiles.find((p) => Number(p.id) === Number(profileItem.id));
            if (clonedFrom !== undefined && clonedFrom !== null) {
                if (clonedFrom > -1) {
                    originalProfile = clonedFrom;
                } else {
                    const conf = this.configurationsService.conf.Current;
                    if (conf && Array.isArray(conf.UsedProfiles)) {
                        parent = conf.UsedProfiles.find((p: any) => Number(p.id) === Number(clonedFrom));
                    }
                    if (!parent && this.pageData && Array.isArray(this.pageData.profiles)) {
                        parent = this.pageData.profiles.find((p: any) => Number(p.id) === Number(clonedFrom));
                    }
                    if (parent) {
                        originalProfile = parent.originalProfile;
                    }
                }
            }
            (profile as any).originalProfile = originalProfile;
        }

        if (profile?.type ==='frame') {
            invalidAccessoriesToFrameProfile = this.checkIfAccessoryMatchFrameProfile(profileItem);
        }

        if (invalidAccessoriesToFrameProfile && invalidAccessoriesToFrameProfile.length && profile?.type === 'frame' ) {
            this.showInvalidAccessoriesForFrameInfo(invalidAccessoriesToFrameProfile, profileItem);
        } else {
            if (profile) {
                const matchedCasingsToFrame = this.profilesService.matchProfileToFrame(profile.id, 'casing');
                const matchedPortalsToFrame = this.profilesService.matchProfileToFrame(profile.id, 'portal');
                const isVariableWidthMuntinProfile =
                    !!profile.widthOutType
                    && !!this.pageData.mullionId
                    && profile.type !== 'coupling'
                    && (profile as any).originalType !== 'coupling';
                const isVariableWidthFrameProfile =
                    !!profile.widthOutType
                    && profile.type === 'frame';
                const shouldDeferVariableWidthToOptions =
                    (profile.type === 'coupling'
                        || isVariableWidthMuntinProfile
                        || isVariableWidthFrameProfile)
                    && profile.widthOutType
                    && !this.isApplyingVariableCouplingFromOptions;

                if (shouldDeferVariableWidthToOptions) {
                    const minWidth = Number(profile.widthOutMin);
                    const initialSelectedWidth = Number(
                        profileItem.selectedWidth
                        ?? profile.selectedWidth
                        ?? profile.widthOut
                        ?? profile.widthOutMin
                    );

                    profile.selectedWidth = Number.isFinite(initialSelectedWidth) && initialSelectedWidth > 0
                        ? initialSelectedWidth
                        : (Number.isFinite(minWidth) && minWidth > 0 ? minWidth : profile.widthOutMin);
                    profile.widthOut = profile.selectedWidth;
                    profile.angle = coupling?.angle ?? this.pageData?.selectedProfile?.angle ?? profile.angle;
                }

                const isVariableWidthCoupling = profile.type === 'coupling' && profile.widthOutType;
                if (!isVariableWidthCoupling) {
                    profile.width = profileItem.width || profile.width;
                }
                if ((profile.type === 'frame' || profile.type === 'threshold')
                    && (profileItem as any).reinforcement !== undefined) {
                    const itemReinforcement = (profileItem as any).reinforcement;
                    if (itemReinforcement && itemReinforcement.automaticSelection === false) {
                        profile.reinforcement = { ...core.copy(itemReinforcement), automaticSelection: false };
                    } else {
                        profile.reinforcement = itemReinforcement ? core.copy(itemReinforcement) : null;
                    }
                }
                let previousOuter: number | undefined;
                if (profile.widthOutType && !shouldDeferVariableWidthToOptions && (profile.type === 'coupling' || this.pageData.mullionId || profile.type === 'frame') && profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined) {
                    previousOuter = profile.widthOut ?? profile.selectedWidth ?? profile.widthOutMin ?? null;
                    if (this.pageData.selectedProfile && this.pageData.selectedProfile.originalProfile < 0 && profile.type !== 'frame') {
                        previousOuter = this.pageData.selectedProfile.widthOut ?? this.pageData.selectedProfile.selectedWidth ?? this.pageData.selectedProfile.widthOutMin ?? this.pageData.selectedProfile.widthOutMax ?? this.pageData.selectedProfile.width;
                        }

                    if (profile.type === 'frame' && conf && this.pageData.frame && Number.isFinite(this.pageData.framePosition)) {
                        const targetFrame = conf.Frames?.find(
                            (f: any) => Number(f.id) === Number(this.pageData.frame.id)
                        );
                        const frameSide = targetFrame?.frame?.[this.pageData.framePosition as number];
                        const frameSideProfileId = Number(frameSide?.alt?.profileId ?? frameSide?.profileId);
                        const frameSideProfile = conf.UsedProfiles?.find(
                            (p: any) => Number(p.id) === frameSideProfileId
                        );

                        const framePreviousOuter = Number(
                            frameSideProfile?.selectedWidth
                            ?? frameSideProfile?.widthOut
                            ?? frameSide?.selectedWidth
                            ?? frameSide?.widthOut
                        );

                        if (Number.isFinite(framePreviousOuter) && framePreviousOuter > 0) {
                            previousOuter = framePreviousOuter;
                        }
                    }

                    if (this.pageData.mullionId && conf) {
                        const sashesMullions = (conf.Sashes || []).reduce((acc: any[], sash: any) => acc.concat(sash?.intMullions || []), [] as any[]);
                        const targetMullion = sashesMullions
                            .concat(conf.Mullions || [])
                            .find((m: any) => Number(m.id) === Number(this.pageData.mullionId));
                        if (targetMullion) {
                            const directionalOuter = targetMullion.direction === 'horizontal'
                                ? targetMullion.rHeight
                                : targetMullion.rWidth;
                            const mullionOuter = targetMullion.selectedWidth ?? targetMullion.widthOut ?? directionalOuter;
                            const numericMullionOuter = Number(mullionOuter);
                            const minAllowedOuter = Number(profileItem.widthOutMin ?? profile.widthOutMin ?? 0);
                            const isValidMullionOuter = Number.isFinite(numericMullionOuter)
                                && numericMullionOuter > 0
                                && (
                                    !Number.isFinite(minAllowedOuter)
                                    || minAllowedOuter <= 0
                                    || numericMullionOuter >= minAllowedOuter
                                    || !previousOuter
                                    || previousOuter <= 0
                                );
                            if (isValidMullionOuter) {
                                previousOuter = numericMullionOuter;
                            }
                        }
                    }

                    if ((!previousOuter || previousOuter <= 0) && profile.type !== 'coupling') {
                        const conf = this.configurationsService.conf.Current;
                        const sashesMullions = (conf.Sashes || []).reduce((acc: any[], sash: any) => acc.concat(sash?.intMullions || []), [] as any[]);
                        const targetMullion = sashesMullions
                            .concat(conf.Mullions || [])
                            .find((m: any) => Number(m.id) === Number(this.pageData.mullionId));
                        if (targetMullion) {
                            previousOuter = targetMullion.direction === 'horizontal' ? targetMullion.rHeight ?? 0 : targetMullion.rWidth ?? 0;
                        }
                    }
                }
                if (profile.widthOutType && !shouldDeferVariableWidthToOptions && profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined) {
                    profile.widthOut = profileItem.selectedWidth;
                    (profile as any).selectedWidth = profileItem.selectedWidth;
                    const conf = this.configurationsService.conf.Current;
                    if (conf && Array.isArray(conf.UsedProfiles)) {
                        const updated = conf.UsedProfiles.find(p => p.id === profile.id);
                    }
                    if(profileItem.angle !== null && profileItem.angle !== undefined) {
                        profile.angle = profileItem.angle;
                    }
                } else if (profile.widthOutType && !shouldDeferVariableWidthToOptions) {
                    if (profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined) {
                        profile.widthOut = profileItem.selectedWidth;
                        (profile as any).selectedWidth = profileItem.selectedWidth; 
                    }
       
                }
                if (profile.widthOutType && !shouldDeferVariableWidthToOptions && profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined && previousOuter !== undefined) {
                    let delta;
                    delta = profileItem.selectedWidth - previousOuter;
                    if (delta !== 0 ) {
                        const selectedWidthForUpdate = Number(profileItem.selectedWidth);
                        // Dla frame'a z widthOutType - sklonuj profil zanim updateProfileValues()
                        if (profile.type === 'frame' && conf && this.pageData.frame) {
                            const framePosition = this.pageData.framePosition;
                            if (Number.isFinite(framePosition)) {
                                const currentFrameProfile = this.pageData.frame.frame?.[framePosition];
                                const currentProfileId = Number(currentFrameProfile?.profileId);
                                const isAlreadyCloned = Number.isFinite(currentProfileId) && currentProfileId < 0;
                                
                                // Jeśli jeszcze nie sklonowany, sklonuj teraz
                                if (!isAlreadyCloned && profile.id > 0) {
                                    const cloned = ProfilesUpdateService.cloneVariableWidthProfile(
                                        profile,
                                        conf?.UsedProfiles,
                                    );
                                    if (cloned) {
                                        // Klon został już dodany do conf.UsedProfiles wewnątrz cloneVariableWidthProfile
                                        profile = cloned; // Pracuj na sklonowanym profilu
                                    }
                                }
                            }
                        }
                        this.windowProfilesUpdateService.updateProfileValues(
                            profile,
                            delta,
                            coupling,
                            Number.isFinite(selectedWidthForUpdate)
                                ? selectedWidthForUpdate
                                : this.clampedWidth,
                            this.pageData,
                        );
                    }
                }

                if (profile?.notStandardLength) {
                    profile.isDefaultLength = this.pageData?.isDefaultLength === false ? false : true;
                    profile.length = this.pageData?.length ?? 0;
                }
                const hasVariableWidthRanges = this.getNormalizedWidthOutRanges(profile.widthOutRanges).length > 0;
                let shouldOpenCouplingColorOptions = false;
                if(profile.type === 'coupling'
                    && profile.widthOutType
                    && hasVariableWidthRanges
                    && !this.isApplyingVariableCouplingFromOptions)
                    {
                        shouldOpenCouplingColorOptions = true
                    }
                const shouldOpenProfileOptions = !this.isApplyingVariableCouplingFromOptions && (
                    (profile.type === 'frame' &&
                        (matchedCasingsToFrame.length > 0 ||
                            matchedPortalsToFrame.find((p) => this.doorPortalsService.isPortalValid(p?.id)
                            ))) ||
                    profile.type === 'extension' ||
                    profile.type === 'sandwich' ||
                    (profile.type === 'coupling' && this.showColorsWhenCoupling) ||
                    shouldDeferVariableWidthToOptions ||
                    shouldOpenCouplingColorOptions ||
                    this.CurConfService.conf === 'complementary_goods' ||
                    hasVariableWidthRanges
                );

                if (shouldOpenProfileOptions) {
                    const priceBaseType = this.getEffectiveOriginalType(profile, this.configurationsService.conf.Current, this.pageData.mullionId);
                    this.selectedColors$.next(profile.selectedColor);
                    this.modalService
                        .open({
                            pageComponent: ProfileOptionsPageComponent,
                            resolve: {
                                profile,
                                priceBaseType,
                                profilesPrices: this.pageData.profilesPrices,
                                profile$: new BehaviorSubject({
                                    count: 1,
                                    currentLength: 0,
                                }),
                                selectedColors$: this.selectedColors$,
                                colorGroups: this.configuratorsDataService.data.windowColorGroups,
                            },
                        })
                        .result.then((result) => {
                            if (shouldDeferVariableWidthToOptions && result) {
                                const replayItem: iccProfileListItem = {
                                    ...profileItem,
                                    selectedWidth: (result as any).selectedWidth,
                                    angle: (result as any).angle,
                                } as iccProfileListItem;

                                this.isApplyingVariableCouplingFromOptions = true;
                                try {
                                    this.changeProfile(replayItem);
                                } finally {
                                    this.isApplyingVariableCouplingFromOptions = false;
                                }
                                return;
                            }
                            this.sharedFacade.closePage(result);
                        });
                } else {
                    // Dla frame'a z widthOutType - wywoływaj setFrameProfile z clone=true
                    if (profile?.type === 'frame' && profile?.widthOutType && conf && this.pageData.frame && Number.isFinite(this.pageData.framePosition)) {
                        this.profilesService.setFrameProfile(
                            conf,
                            profile,
                            this.pageData.frame,
                            this.pageData.framePosition,
                            {
                                clone: true,
                            }
                        );
                    }
                    this.sharedFacade.closePage(profile);
                }
            }
        }
    }

    selectProfile(profileItem: iccProfileListItem) {
        this.changeProfile(profileItem);
    }

    private checkIfAccessoryMatchFrameProfile(profileItem: iccProfileListItem) {
        if (this.CurConfService.conf !== 'complementary_goods' && this.CurConfService.conf !== 'coupled_window' && this.CurConfService.conf !== 'roller_shutter') {
            return this.profilesService.getNonMatchingAccessoriesToTheFrame(Number(profileItem.id));
        } else {
            return [];
        }
    }

    clickShowInfoButton(item: iccListItem) {
        const profile = this.profilesService.getProfile(Number(item?.id));
        profile && this.modalService
            .open({
                pageComponent: ProfileInfoComponent,
                resolve: {
                    profile: () => profile,
                    button: () => this.isSelectProfileButtonInShowInfoButtonAvailable(Number(item?.id))
                },
            });
    }

    isSelectProfileButtonInShowInfoButtonAvailable(profileId: number) {
        const isPortalAvailableInFrame = Boolean(this.profilesService.matchProfileToFrame(profileId, 'portal')?.length)
        const isCasingAvailableInFrame = Boolean(this.profilesService.matchProfileToFrame(profileId, 'casing')?.length)

        return !(isCasingAvailableInFrame || isPortalAvailableInFrame);
    }

    updateProfilePrice(item: iccProfileListItem) {
        if (!item.widthOutType || !item.selectedWidth) {
            return;
        }

        const profileId = Number(item.id);
        const profile = this.pageData.profiles.find((p) => Number(p.id) === profileId);
        if (!profile) {
            return;
        }
        const previousOuter = Number.isFinite(Number(profile.widthOut)) ? Number(profile.widthOut) : 0;
        const { clampedWidth, rangeIndex } = this.getProfileWidthAndRange(profile, item.selectedWidth);
        this.clampedWidth = clampedWidth;
        if (!this.clampedWidth) {
            return;
        }
        if (this.clampedWidth !== item.selectedWidth) {
            item.selectedWidth = this.clampedWidth;
        }
        
        if (profile.widthOutType) {
            profile.widthOut = this.clampedWidth;
            (profile as any).selectedWidth = this.clampedWidth;

            item.description = `${profile.name}${this.clampedWidth ? ` ${this.clampedWidth}mm` : ''}`;
            const profileItem = this.profiles.find((p) => p.id === profileId);
            if (profileItem) {
                profileItem.selectedWidth = this.clampedWidth;
                profileItem.description = item.description;
            }
        }

        let dynamicType: string = Common.isArray(this.pageData.type) ? item.originalType : this.pageData.type;
        if (profile.widthOutType && rangeIndex !== -1) {
            dynamicType = `${item.originalType}_range_${rangeIndex}`;
        }
        if (item.originalType === 'coupling' || item.type === 'coupling') {
            const couplingPriceSystem = this.CurConfService.conf === 'coupled_window'
                ? null
                : this.pageData.system;
            item.price = this.profilesPriceService.getProfilePrice(
                profileId,
                'coupling',
                couplingPriceSystem,
                item.selectedColor,
                this.pageData.profilesPrices,
                undefined,
                undefined,
                undefined,
                undefined,
                this.clampedWidth,
                {
                    ...profile,
                    selectedWidth: this.clampedWidth,
                    widthOut: this.clampedWidth,
                }
            );
        } else {
            const colorFactor = this.priceColorsService.getSideProfileColorFactor({
                profile,
                profileColors: item.selectedColor,
                wood: item.selectedWood,
                system: this.configurationsService.conf.Current.System,
                colorGroups: this.configuratorsDataService.data.windowColorGroups || [],
            });
            item.price = this.profilesPriceService.getProfilePrice(
                profileId,
                dynamicType,
                this.pageData.system,
                item.selectedColor,
                this.pageData.profilesPrices,
                undefined,
                undefined,
                undefined,
                colorFactor,
                this.clampedWidth,
                { ...profile, originalProfile: (item as any).originalProfile, originalType: item.originalType }
            );
        }

        if (item.widthOutType && item.price) {
            ['price_field', 'price_piece', 'price_construction'].forEach((field) => {
                if (item.price[field] == null) {
                    item.price[field] = 0;
                }
            });
        }
        profile.widthOut = previousOuter;
        
    }



    getPendingCouplingClone(key: string = 'next'): { profileId: number; widthSettings: any } | undefined {
        return this.pendingCouplingClones.get(key);
    }

    clearPendingCouplingClone(key: string = 'next') {
        this.pendingCouplingClones.delete(key);
    }

    public hasPendingCouplingClones(): boolean {
        return this.pendingCouplingClones.size > 0;
    }

    public getAllPendingCouplingClones(): Map<string, { profileId: number; widthSettings: any }> {
        return new Map(this.pendingCouplingClones);
    }


}

